import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useValidatableFormFieldFocus,
  useFormFieldMainValidation,
  useFormFieldValidationWithOnValueChangeValidation,
} from '../../../hooks';
import { FormFieldWrap } from '@zooplus/ui-components';
import {
  FieldPropsExternalValidation,
  FieldPropsProcessing,
  FieldPropsWithOnChange,
  FieldPropsWithOnFocus,
} from './types';
import { CompositeOrderedValidator } from '../../../hooks/validation/validator/CompositeOrderedValidator';
import InputWithLoader from '../input-with-loader/InputWithLoader';
import { OrderNumberValidator } from '../../../hooks/validation/validator/OrderNumberValidator';
import { OrderSelector } from '../order-selector';
import { OrderNumberFormatValidator } from '../../../hooks/validation/validator/OrderNumberFormatValidator';
import { OrderCompositeField } from '../../../hooks/validation/types';
import { KeycloakContext } from '../../context/KeycloakContext';

type OrderNumberFieldProps = FieldPropsWithOnChange &
  FieldPropsWithOnFocus &
  FieldPropsProcessing &
  FieldPropsExternalValidation & {
    setOrderIdValidatedAndValid: (value: boolean) => void;
  };

const OrderNumberField: React.FC<OrderNumberFieldProps> = ({
  onChange,
  setOrderIdValidatedAndValid,
  onFocus,
  externalValidator,
  isProcessing: isProcessingExternally,
}: any) => {
  const keycloakContext = useContext(KeycloakContext);
  const isUserLoggedIn = !!keycloakContext.tokenData?.customerId;

  const { t } = useTranslation();

  const label = t('form.fields.order_number.label');
  const placeholder = t('form.fields.order_number.placeholder');
  const info = t('form.fields.order_number.info');
  const isRequired = false;
  const minLength = 1;
  const maxLength = 15;
  const [isProcessing, setIsProcessing] = useState(false);
  const [isProcessingInternally, setIsProcessingInternally] = useState(false);
  const [value, setValue] = useState<OrderCompositeField>({ orderId: '', suborderId: '' });
  const inputWrapperElementRef = useRef<HTMLDivElement>(null);
  const basicFieldValidator = new OrderNumberFormatValidator(isRequired, minLength, maxLength);

  const asyncFieldValidator = new OrderNumberValidator(isUserLoggedIn);

  useEffect(() => {
    setIsProcessing(isProcessingInternally || isProcessingExternally);
  }, [isProcessingInternally, isProcessingExternally]);

  useEffect(() => {
    setOrderIdValidatedAndValid(false);
    onChange(value);
  }, [value]);

  const { hasError: staticValidatorHasError } = useFormFieldValidationWithOnValueChangeValidation(
    basicFieldValidator,
    { value },
  );
  const {
    hasError: fullValidatorHasError,
    errorTags: fullValidationErrorTags,
    validate: runFullValidation,
  } = useFormFieldMainValidation(
    new CompositeOrderedValidator(basicFieldValidator, asyncFieldValidator, externalValidator),
    { value },
  );
  const [hasError, setHasError] = useState(false);
  useEffect(() => {
    setHasError(staticValidatorHasError || fullValidatorHasError);
  }, [staticValidatorHasError, fullValidatorHasError]);
  const [errorMessage, setErrorMessage] = useState('');
  useEffect(() => {
    const key = fullValidationErrorTags[0] || 'general';
    setErrorMessage(t(`form.fields.order_number.error_messages.${key}`));
  }, [fullValidationErrorTags]);

  useValidatableFormFieldFocus({
    formFieldSelector: 'input',
    formFieldWrapperElementRef: inputWrapperElementRef,
  });

  const updateValueCallback = useCallback((evt) => {
    const suborderId = evt.target.value || '';
    const hyphenIndex = suborderId.indexOf('-');
    const orderId = suborderId.substring(0, hyphenIndex >= 0 ? hyphenIndex : undefined);
    setValue({ orderId, suborderId });
  }, []);
  const onBlurCallback = useCallback(async () => {
    setIsProcessingInternally(true);
    await runFullValidation().then((result) => {
      setOrderIdValidatedAndValid(result.isValid);
    });
    setIsProcessingInternally(false);
  }, [runFullValidation]);

  return (
    <div
      data-test="contactForm.field.orderId"
      data-form-control-type="TEXT"
      className="contactForm__field">
      <FormFieldWrap
        labelFor="order-id"
        isRequired={isRequired}
        labelText={label}
        infoMessage={info}
        errorMessage={hasError && errorMessage}
        ref={inputWrapperElementRef}>
        <InputWithLoader isLoading={isProcessing} isDisabled={isProcessing}>
          <OrderSelector
            hasError={hasError}
            onFocus={onFocus}
            isDisabled={isProcessing}
            setValue={setValue}
            inputProps={{
              id: 'suborder-id',
              placeholder: placeholder,
              name: 'suborderNumber',
              type: 'text',
              minLength: minLength,
              maxLength: maxLength,
              value: value.suborderId,
              onChange: updateValueCallback,
              onBlur: onBlurCallback,
              onKeyDown: (e) => {
                if (!/\d|-|[A-Z]/.test(e.key)) {
                  e.preventDefault();
                }
              },
            }}
          />
        </InputWithLoader>
      </FormFieldWrap>
      <input type="text" hidden value={value.orderId} name="orderNumber" id="order-id" readOnly />
    </div>
  );
};

export default OrderNumberField;
